import { useAtom } from '@reatom/npm-react'
import React, { useEffect, useMemo } from 'react'
import { useLatest } from 'react-use'
import styled from 'styled-components'
import { match, P } from 'ts-pattern'

import { useTranslation } from '@root/shared/localization'
import { Snackbar } from '@root/shared/ui-kit/snackbar/snackbar'

import { SellEntirePositionModel } from '../../sell-position/atoms/multiple-swap-requests-model'

import { SellPositionSnackbarItem } from './sell-position-snackbar-item'

import type { SnackbarProps } from '@root/shared/ui-kit/snackbar/snackbar'

type SellPositionSnackbarProps = {
  onClose?: () => void
  request: SellEntirePositionModel
}

export const SellPositionSnackbar = ({
  onClose,
  request,
}: SellPositionSnackbarProps) => {
  const { t } = useTranslation()
  const [stage] = useAtom(request.stageAtom)
  const handleCloseRef = useLatest(onClose)

  const snackbarProps = useMemo(() => {
    return match(stage)
      .returnType<SnackbarProps>()
      .with(P.instanceOf(SellEntirePositionModel.StageSending), () => ({
        intent: 'info',
        icon: 'circular-loading',
        children: t('trading.sell-position-snackbar.pending'),
      }))
      .with(P.instanceOf(SellEntirePositionModel.StageSent), () => ({
        intent: 'info',
        icon: 'circular-loading',
        children: t('trading.sell-position-snackbar.pending'),
      }))
      .with(P.instanceOf(SellEntirePositionModel.StageSettled), (stage) => {
        const allFinished = stage.txs.every(
          ({ status }) => status === 'finished',
        )
        if (allFinished) {
          return {
            intent: 'success',
            icon: 'success',
            children: t('trading.sell-position-snackbar.success'),
          }
        }
        const allFailed = stage.txs.every(({ status }) => status === 'failed')
        if (allFailed) {
          return {
            intent: 'error',
            icon: 'error',
            children: t('trading.sell-position-snackbar.failed'),
          }
        }
        return {
          intent: 'warning',
          icon: 'warning',
          children: t('trading.sell-position-snackbar.successSome'),
        }
      })
      .with(P.instanceOf(SellEntirePositionModel.StageFailed), () => ({
        intent: 'error',
        icon: 'error',
        children: t('trading.sell-position-snackbar.failed'),
      }))
      .exhaustive()
  }, [stage, t])

  const [positionsByWallet] = useAtom(request.position.positionsByWallet)
  const items = useMemo(() => {
    const result = []
    for (const position of positionsByWallet.values()) {
      if (stage instanceof SellEntirePositionModel.StageSending) {
        result.push({ position, tx: undefined })
      } else {
        result.push({
          position,
          tx: stage.txs.find((it) => it.walletId === position.walletId),
        })
      }
    }

    return result
  }, [positionsByWallet, stage])

  const shouldScheduleClose = useMemo(
    () =>
      match(stage)
        .returnType<boolean>()
        .with(P.instanceOf(SellEntirePositionModel.StageSending), () => false)
        .otherwise(() => true),
    [stage],
  )

  useEffect(() => {
    if (shouldScheduleClose) {
      setTimeout(() => {
        handleCloseRef.current?.()
      }, 5000)
    }
  }, [shouldScheduleClose, handleCloseRef])

  return (
    <Root
      intent={snackbarProps.intent}
      icon={snackbarProps.icon}
      onClose={onClose}
      description={
        <>
          <Body>
            {items.map(({ position, tx }, index, arr) => (
              <React.Fragment key={position.id}>
                <SellPositionSnackbarItem position={position} tx={tx} />
                {index < arr.length - 1 && <Separator />}
              </React.Fragment>
            ))}
          </Body>
        </>
      }
    >
      {snackbarProps.children}
    </Root>
  )
}

const Root = styled(Snackbar)`
  padding-inline: 12px 12px;
`

const Body = styled.div`
  padding-block: 8px 0px;
  display: grid;
  gap: 9px;
`

const Separator = styled.div`
  background: var(--backgroundA-2);

  border-radius: 2px;
  height: 1px;
  width: 100%;
`
